import type { DragEvent, FC, ReactNode } from 'react'

import { useEffect, useId, useState } from 'react'
import { CircleAlert, CheckCircle2, Loader2, Upload } from 'lucide-react'
import { cn } from '@/lib/utils'

export const inputClass = 'w-full rounded-xl border border-white/10 bg-[#0a1424] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cyan-300/60 focus:outline-none focus:ring-2 focus:ring-cyan-300/20 disabled:opacity-50'

export const primaryButton = 'inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-300 px-5 py-3 text-sm font-bold text-[#06121f] transition hover:bg-cyan-200 disabled:cursor-not-allowed disabled:opacity-50'

export const secondaryButton = 'inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 px-5 py-3 text-sm font-bold text-white transition hover:border-cyan-300/50 disabled:cursor-not-allowed disabled:opacity-50'

export const useObjectUrl = (blob: Blob | null): string | null => {
    const [url, setUrl] = useState<string | null>(null)

    useEffect(() => {
        if (!blob) {
            setUrl(null)
            return
        }
        const next = URL.createObjectURL(blob)
        setUrl(next)
        return () => URL.revokeObjectURL(next)
    }, [blob])

    return url
}

interface FilePickerProps {
    accept: string
    multiple?: boolean
    label: string
    hint?: string
    onFiles: (files: File[]) => void
}

export const FilePicker: FC<FilePickerProps> = ({ accept, multiple = false, label, hint, onFiles }): ReactNode => {
    const id = useId()
    const [dragging, setDragging] = useState(false)

    const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
        event.preventDefault()
        setDragging(false)
        const files = Array.from(event.dataTransfer.files)
        if (files.length) onFiles(multiple ? files : files.slice(0, 1))
    }

    return (
        <label
            htmlFor={id}
            onDragOver={(event) => {
                event.preventDefault()
                setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            className={cn(
                'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-10 text-center transition',
                dragging ? 'border-cyan-300 bg-cyan-300/5' : 'border-white/15 hover:border-cyan-300/50 hover:bg-white/[0.02]'
            )}
        >
            <span className='rounded-xl bg-cyan-400/10 p-3 text-cyan-300'><Upload className='size-5' /></span>
            <span className='font-bold text-white'>{label}</span>
            <span className='text-sm text-slate-400'>{hint ?? 'Or drag and drop here'}</span>
            <input
                id={id}
                type='file'
                accept={accept}
                multiple={multiple}
                className='sr-only'
                onChange={(event) => {
                    const files = Array.from(event.target.files ?? [])
                    if (files.length) onFiles(files)
                    event.target.value = ''
                }}
            />
        </label>
    )
}

interface FieldProps {
    label: string
    htmlFor?: string
    children: ReactNode
    className?: string
}

export const Field: FC<FieldProps> = ({ label, htmlFor, children, className }): ReactNode => (
    <div className={className}>
        <label htmlFor={htmlFor} className='mb-2 block text-sm font-medium text-slate-300'>{label}</label>
        {children}
    </div>
)

interface ChoiceOption<T extends string> {
    value: T
    label: string
}

interface ChoiceGroupProps<T extends string> {
    label: string
    options: ChoiceOption<T>[]
    value: T
    onChange: (value: T) => void
}

export const ChoiceGroup = <T extends string>({ label, options, value, onChange }: ChoiceGroupProps<T>): ReactNode => (
    <fieldset>
        <legend className='mb-2 block text-sm font-medium text-slate-300'>{label}</legend>
        <div className='flex flex-wrap gap-2'>
            {options.map((option) => (
                <button
                    key={option.value}
                    type='button'
                    aria-pressed={value === option.value}
                    onClick={() => onChange(option.value)}
                    className={cn(
                        'rounded-lg border px-3.5 py-2 text-sm font-medium transition',
                        value === option.value
                            ? 'border-cyan-300 bg-cyan-300/10 text-cyan-200'
                            : 'border-white/10 text-slate-300 hover:border-white/30'
                    )}
                >
                    {option.label}
                </button>
            ))}
        </div>
    </fieldset>
)

interface StatsProps {
    items: { label: string, value: string }[]
}

export const Stats: FC<StatsProps> = ({ items }): ReactNode => (
    <dl className='grid grid-cols-2 gap-3 sm:grid-cols-4'>
        {items.map((item) => (
            <div key={item.label} className='rounded-xl border border-white/10 bg-[#0a1424] px-4 py-3'>
                <dt className='text-xs text-slate-500'>{item.label}</dt>
                <dd className='mt-1 font-bold text-white'>{item.value}</dd>
            </div>
        ))}
    </dl>
)

interface StatusProps {
    tone: 'idle' | 'working' | 'success' | 'error'
    children: ReactNode
}

export const Status: FC<StatusProps> = ({ tone, children }): ReactNode => (
    <p
        role={tone === 'error' ? 'alert' : 'status'}
        className={cn(
            'flex items-start gap-2 text-sm',
            tone === 'error' && 'text-rose-300',
            tone === 'success' && 'text-emerald-300',
            tone === 'working' && 'text-cyan-200',
            tone === 'idle' && 'text-slate-400'
        )}
    >
        {tone === 'working' && <Loader2 className='mt-0.5 size-4 shrink-0 animate-spin' />}
        {tone === 'success' && <CheckCircle2 className='mt-0.5 size-4 shrink-0' />}
        {tone === 'error' && <CircleAlert className='mt-0.5 size-4 shrink-0' />}
        <span>{children}</span>
    </p>
)

interface PreviewProps {
    blob: Blob | null
    alt: string
    empty?: string
}

export const Preview: FC<PreviewProps> = ({ blob, alt, empty = 'Your image preview will appear here.' }): ReactNode => {
    const url = useObjectUrl(blob)

    return (
        <div className='flex min-h-56 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-[repeating-conic-gradient(#0d1a2c_0%_25%,#0a1424_0%_50%)] bg-[length:20px_20px] p-4'>
            {url
                ? <img src={url} alt={alt} className='max-h-[420px] w-auto max-w-full rounded-lg object-contain' />
                : <p className='text-sm text-slate-500'>{empty}</p>}
        </div>
    )
}

export const PrivacyNote: FC = (): ReactNode => (
    <p className='text-xs text-slate-500'>Local by design. Your files stay in this browser while they are processed.</p>
)

export const errorMessage = (error: unknown): string => (
    error instanceof Error && error.message ? error.message : 'Something went wrong. Please try another file.'
)
